import { useNavigate } from 'react-router-dom'
import { Calendar, Users, Shield, ArrowRight, Zap, BarChart3, MessageCircle, Building2, CheckCircle2, XCircle, ChevronDown, Check } from 'lucide-react'
import { useState } from 'react'
import { useAuthStore } from '@/store/authStore'

export default function LandingPage() {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const [openFaq, setOpenFaq] = useState<number | null>(null)

    const toggleFaq = (index: number) => {
        setOpenFaq(openFaq === index ? null : index)
    }

    return (
        <div className="min-h-screen bg-[#001c28] text-white font-sans selection:bg-[#d4a94d]/30 overflow-x-hidden">

            {/* WhatsApp Floating Button */}
            <a
                href="https://wa.me/554998208388?text=Olá! Gostaria de mais informações sobre o SpaceRub."
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-10 right-10 z-[100] bg-[#25D366] text-white p-4 rounded-full shadow-[0_0_20px_rgba(37,211,102,0.4)] hover:scale-110 hover:shadow-[0_0_30px_rgba(37,211,102,0.6)] transition-all duration-300 group flex items-center justify-center border-2 border-white/10"
            >
                <MessageCircle className="w-8 h-8" />
                <span className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-[#001c28] text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-[#d4a94d]/30">
                    Fale Conosco
                </span>
            </a>

            {/* Navigation - White Background */}
            <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm transition-all duration-300">
                <div className="max-w-7xl mx-auto px-6 h-36 flex items-center justify-between">
                    <div className="flex items-center space-x-4 cursor-pointer" onClick={() => navigate('/')}>
                        {/* Logo 30% bigger (h-24 -> h-32) */}
                        <img src="https://apprub.com.br/space/logo.png" alt="SpaceRub Logo" className="h-32 w-auto" />
                    </div>

                    <div className="hidden lg:flex items-center space-x-8">
                        {['Recursos', 'Ambientes', 'FAQ'].map((item) => (
                            <a
                                key={item}
                                href={`#${item.toLowerCase()}`}
                                className="text-xs font-bold uppercase tracking-widest text-[#001c28]/60 hover:text-[#001c28] transition-colors"
                            >
                                {item}
                            </a>
                        ))}

                        <div className="w-px h-4 bg-gray-200 mx-4"></div>

                        {user ? (
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="text-xs font-bold uppercase tracking-widest bg-[#001c28] text-white px-6 py-3 rounded-xl shadow-lg shadow-[#001c28]/20 hover:bg-[#d4a94d] hover:text-[#001c28] hover:-translate-y-1 transition-all active:scale-95 border-2 border-[#001c28] hover:border-[#d4a94d]"
                            >
                                Ir para Dashboard
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="text-xs font-bold uppercase tracking-widest text-[#001c28] hover:text-[#d4a94d] transition-colors"
                                >
                                    Login
                                </button>
                                <button
                                    onClick={() => navigate('/signup')}
                                    className="text-xs font-bold uppercase tracking-widest bg-[#001c28] text-white px-6 py-3 rounded-xl shadow-lg shadow-[#001c28]/20 hover:bg-[#d4a94d] hover:text-[#001c28] hover:-translate-y-1 transition-all active:scale-95 border-2 border-[#001c28] hover:border-[#d4a94d]"
                                >
                                    Criar Conta
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </nav>

            {/* Hero Section - Block Blue */}
            <section className="relative pt-48 pb-24 px-6 overflow-hidden bg-[#001c28]">
                {/* Background Effects */}
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#d4a94d]/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#d4a94d]/5 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/4 pointer-events-none"></div>
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8 animate-in fade-in slide-in-from-left-8 duration-1000">
                            <div className="inline-flex items-center space-x-3 bg-white/5 border border-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                                <span className="flex h-2 w-2 rounded-full bg-[#d4a94d] animate-pulse"></span>
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#d4a94d]">O Sistema Definitivo</span>
                            </div>

                            <div className="space-y-4">
                                {/* Added pr-8 py-2 to prevent clipping of 'Fronteiras' */}
                                <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[1.1] uppercase italic text-white pr-8 py-2">
                                    Gestão de Espaços <br />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4a94d] to-[#f3d99e]">Sem Fronteiras</span>
                                </h1>
                                <p className="text-base text-gray-400 font-medium max-w-lg leading-relaxed">
                                    Centralize reservas, Automatize cobranças e encante seus clientes com uma experiência <span className="text-white font-bold">Premium</span>.
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-5">
                                {user ? (
                                    <button
                                        onClick={() => navigate('/dashboard')}
                                        className="group bg-[#d4a94d] text-[#001c28] px-8 py-4 rounded-xl font-black uppercase tracking-[0.2em] text-[10px] flex items-center justify-center space-x-3 shadow-[0_0_40px_-10px_rgba(212,169,77,0.3)] hover:bg-[#e5b85a] hover:shadow-[0_0_60px_-15px_rgba(212,169,77,0.5)] transition-all duration-300 ring-4 ring-transparent hover:ring-[#d4a94d]/30"
                                    >
                                        <span>Acessar Meu Painel</span>
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => navigate('/signup')}
                                            className="group bg-[#d4a94d] text-[#001c28] px-8 py-4 rounded-xl font-black uppercase tracking-[0.2em] text-[10px] flex items-center justify-center space-x-3 shadow-[0_0_40px_-10px_rgba(212,169,77,0.3)] hover:bg-[#e5b85a] hover:shadow-[0_0_60px_-15px_rgba(212,169,77,0.5)] transition-all duration-300 ring-4 ring-transparent hover:ring-[#d4a94d]/30"
                                        >
                                            <span>Começar Agora</span>
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </button>
                                        <button
                                            onClick={() => navigate('/login')}
                                            className="px-8 py-4 rounded-xl border border-white/10 font-black uppercase tracking-[0.2em] text-[10px] text-white hover:bg-white/5 transition-all flex items-center justify-center hover:border-white/30"
                                        >
                                            Login
                                        </button>
                                    </>
                                )}
                            </div>

                            <div className="pt-4 flex items-center space-x-8 text-gray-500">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-[#d4a94d]" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Setup em 2 min</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-[#d4a94d]" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Sem Cartão</span>
                                </div>
                            </div>
                        </div>

                        <div className="relative animate-in fade-in zoom-in duration-1000 delay-200 lg:h-[500px] flex items-center justify-center">
                            {/* Abstract Phone/Dashboard Representation */}
                            <div className="relative w-full aspect-square max-w-sm mx-auto">
                                <div className="absolute inset-0 bg-gradient-to-tr from-[#d4a94d]/20 to-transparent rounded-full blur-[80px] animate-pulse"></div>

                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full">
                                    <div className="bg-[#002b3d]/60 backdrop-blur-xl border border-white/10 p-6 rounded-[2rem] shadow-2xl hover:scale-[1.02] transition-transform duration-700 relative z-20">
                                        <div className="flex items-center justify-between mb-8">
                                            <div>
                                                <div className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-1">Faturamento</div>
                                                <div className="text-2xl font-black text-white">R$ 42.850<span className="text-[#d4a94d]">,00</span></div>
                                            </div>
                                            <div className="w-10 h-10 rounded-xl bg-[#d4a94d]/10 flex items-center justify-center">
                                                <BarChart3 className="w-5 h-5 text-[#d4a94d]" />
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            {[1, 2, 3].map(i => (
                                                <div key={i} className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                                    <div className="h-full bg-[#d4a94d]" style={{ width: `${Math.random() * 40 + 40}%` }}></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="absolute -top-5 -right-5 z-10 animate-bounce delay-700 duration-[3000ms]">
                                    <div className="bg-white p-3 rounded-2xl shadow-xl flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                            <Check className="w-4 h-4 text-green-600" />
                                        </div>
                                        <div>
                                            <div className="text-[8px] text-gray-400 font-bold uppercase">Confirmada</div>
                                            <div className="text-xs font-bold text-[#001c28]">Quadra 01</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="absolute -bottom-0 -left-5 z-30 animate-bounce delay-300 duration-[4000ms]">
                                    <div className="bg-white p-3 rounded-2xl shadow-xl flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                            <Users className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div>
                                            <div className="text-[8px] text-gray-400 font-bold uppercase">Membros</div>
                                            <div className="text-xs font-bold text-[#001c28]">+12 hoje</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Problem vs Solution - Block White */}
            <section className="py-24 bg-white text-[#001c28]">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-400 mb-4">A Evolução</p>
                        <h2 className="text-3xl md:text-4xl font-black uppercase italic text-[#001c28] pr-8 py-2">Pare de perder <span className="text-red-500">tempo</span>. Ganhe <span className="text-[#d4a94d]">controle</span>.</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        {/* Old Way */}
                        <div className="p-10 rounded-[2.5rem] bg-white border border-gray-200 shadow-xl hover:border-red-100 transition-all hover:-translate-y-1">
                            <div className="flex items-center gap-4 mb-6">
                                <XCircle className="w-8 h-8 text-red-500" />
                                <h3 className="text-xl font-black uppercase tracking-wider text-red-400">O Jeito Antigo</h3>
                            </div>
                            <ul className="space-y-4">
                                {['Planilhas confusas e manuais', 'Conflitos de horário constantes', 'Cobranças via Pix pessoal', 'Cliente esperando resposta no WhatsApp'].map(item => (
                                    <li key={item} className="flex items-center gap-3 text-gray-500 text-sm font-medium">
                                        <div className="w-1.5 h-1.5 rounded-full bg-red-400/50"></div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* SpaceRub Way */}
                        <div className="p-10 rounded-[2.5rem] bg-[#001c28] border-4 border-[#d4a94d]/10 shadow-2xl shadow-[#001c28]/10 transform md:scale-105">
                            <div className="flex items-center gap-4 mb-6">
                                <CheckCircle2 className="w-8 h-8 text-[#d4a94d]" />
                                <h3 className="text-xl font-black uppercase tracking-wider text-[#d4a94d]">O SpaceRub</h3>
                            </div>
                            <ul className="space-y-4">
                                {['Agenda digital 100% autônoma', 'Bloqueio automático de conflitos', 'Pagamentos integrados', 'Cliente reserva sozinho'].map(item => (
                                    <li key={item} className="flex items-center gap-3 text-white text-sm font-bold">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#d4a94d]"></div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section - Block Blue */}
            <section id="recursos" className="py-24 relative bg-[#001c28] text-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="max-w-3xl mb-20 space-y-6">
                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#d4a94d]">Funcionalidades</p>
                        {/* Smaller text, Added pr-4 to fix clipping */}
                        <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter text-white leading-none pr-8 py-2">
                            Tudo o que você precisa <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4a94d] to-[#f3d99e]">em um só lugar</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { title: 'Agenda Inteligente', icon: Calendar, desc: 'Visualização mensal, semanal e diária.' },
                            { title: 'Gestão Financeira', icon: Zap, desc: 'Integração direta com Mercado Pago.' },
                            { title: 'Controle de Acesso', icon: Shield, desc: 'Defina quem pode reservar.' },
                            { title: 'Multi-Organização', icon: Building2, desc: 'Gerencie múltiplos complexos.' },
                            { title: 'Dashboards', icon: BarChart3, desc: 'Métricas vitais em tempo real.' },
                            { title: 'Comunicação', icon: MessageCircle, desc: 'Chat interno e Broadcast.' },
                        ].map((f, i) => (
                            <div key={i} className="bg-[#01141d] p-8 rounded-[1.5rem] border border-white/5 hover:border-[#d4a94d]/30 hover:bg-[#002535] hover:-translate-y-1 transition-all duration-300 group">
                                <div className="w-12 h-12 rounded-xl bg-[#002b3d] flex items-center justify-center mb-6 group-hover:bg-[#d4a94d] transition-colors">
                                    <f.icon className="w-6 h-6 text-[#d4a94d] group-hover:text-[#001c28] transition-colors" />
                                </div>
                                <h3 className="text-lg font-black uppercase italic tracking-tighter mb-2 text-white group-hover:text-[#d4a94d] transition-colors">{f.title}</h3>
                                <p className="text-gray-400 font-medium text-xs leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Environments/Photos Section - Block White */}
            <section id="ambientes" className="py-24 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 text-center mb-16 relative z-10">
                    <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#d4a94d] mb-4 relative z-10">Versatilidade</p>
                    {/* Smaller text */}
                    <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter text-[#001c28] leading-none relative z-10 pr-8 py-2">
                        Um sistema para <br /><span className="text-[#001c28]/80">qualquer espaço</span>
                    </h2>
                </div>

                <div className="max-w-[1600px] mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { img: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48', label: 'Esportes', desc: 'Quadras e Complexos' },
                            { img: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72', label: 'Corporativo', desc: 'Salas e Coworking' },
                            // FIXED IMAGE URL - Kids playing (Robust URL)
                            { img: 'https://images.unsplash.com/photo-1526676037777-05a232554f77', label: 'Lazer', desc: 'Salões e Áreas Comuns' },
                            // FIXED IMAGE URL - Corporate Congress
                            { img: 'https://images.unsplash.com/photo-1544531586-fde5298cdd40', label: 'Eventos', desc: 'Auditórios e Teatros' },
                        ].map((item, i) => (
                            <div key={i} className="group relative h-[400px] rounded-[2rem] overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100">
                                <img src={item.img} alt={item.label} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 group-hover:brightness-110 brightness-[0.8]" />

                                <div className="absolute inset-0 bg-gradient-to-t from-[#001c28] via-transparent to-transparent opacity-90"></div>

                                {/* Gold Border Effect */}
                                <div className="absolute inset-0 border-4 border-[#d4a94d] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-[2rem]"></div>

                                <div className="absolute bottom-0 left-0 p-8 transform transition-transform duration-500 group-hover:translate-y-[-5px]">
                                    <div className="w-8 h-1 bg-[#d4a94d] mb-3 transform origin-left transition-transform duration-500 scale-x-0 group-hover:scale-x-100"></div>
                                    <h4 className="text-white font-black uppercase tracking-tighter text-2xl italic leading-none mb-2">{item.label}</h4>
                                    <p className="text-gray-300 font-bold uppercase text-[10px] tracking-widest">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section - Block Blue */}
            <section id="faq" className="py-24 bg-[#001c28] text-white">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#d4a94d] mb-4">Dúvidas Frequentes</p>
                        <h2 className="text-3xl md:text-4xl font-black uppercase text-white pr-4">Perguntas & Respostas</h2>
                    </div>

                    <div className="space-y-4">
                        {[
                            { q: "O SpaceRub serve para o meu condomínio?", a: "Sim! Nossa plataforma é modular e atende perfeitamente condomínios residenciais e comerciais." },
                            { q: "Como funciona o pagamento?", a: "Utilizamos o Mercado Pago para processar pagamentos via Pix e Cartão de Crédito." },
                            { q: "Posso testar antes de contratar?", a: "Com certeza. Oferecemos um período de teste gratuito com acesso total." },
                            { q: "Preciso instalar algo?", a: "Não. O SpaceRub é 100% em nuvem e funciona em qualquer navegador." }
                        ].map((faq, i) => (
                            <div key={i} className="border border-white/5 rounded-2xl bg-[#01141d] overflow-hidden">
                                <button
                                    onClick={() => toggleFaq(i)}
                                    className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                                >
                                    <span className="font-bold text-sm text-gray-200 uppercase tracking-wide">{faq.q}</span>
                                    <ChevronDown className={`w-5 h-5 text-[#d4a94d] transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                                </button>
                                <div className={`px-6 overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40 py-6 border-t border-white/5' : 'max-h-0'}`}>
                                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Final - Modest & Beautiful & ANIMATED & DARK */}
            <section className="py-24 px-6 bg-white">
                {/* Card Background: Gradient Dark Blue + Gold Border + Shadow */}
                <div className="max-w-4xl mx-auto rounded-[3rem] p-16 text-center border-4 border-[#d4a94d] shadow-[0_0_50px_-10px_rgba(212,169,77,0.3)] bg-gradient-to-br from-[#001c28] to-[#01334d] relative overflow-hidden animate-in zoom-in duration-1000">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.05] pointer-events-none"></div>

                    <div className="relative z-10">
                        <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-tighter text-white leading-none mb-10 animate-pulse pr-8 py-2">
                            Pronto para evoluir?
                        </h2>
                        <div className="flex flex-col sm:flex-row justify-center gap-6">
                            <button
                                onClick={() => navigate('/signup')}
                                className="bg-[#d4a94d] text-[#001c28] px-10 py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-lg hover:bg-white hover:text-[#001c28] transition-all duration-300 hover:scale-110 animate-bounce delay-700"
                            >
                                Criar Conta Agora
                            </button>
                            <button
                                onClick={() => navigate('/login')}
                                className="text-white border-2 border-white/20 px-10 py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-xs hover:bg-white/10 transition-all hover:scale-105"
                            >
                                Fazer Login
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer - Blue */}
            <footer className="py-12 bg-[#001c28] border-t border-[#d4a94d]/10 text-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#d4a94d] rounded-xl flex items-center justify-center font-black text-[#001c28]">SR</div>
                            <span className="font-bold text-gray-400">SpaceRub &copy; 2024</span>
                        </div>

                        <div className="text-sm font-bold text-white/70">
                            <a href="https://www.apprub.com.br/spacerub" target="_blank" className="hover:text-[#d4a94d] transition-colors">www.apprub.com.br/spacerub</a>
                        </div>

                        <div className="flex gap-8 text-[10px] font-black uppercase tracking-widest text-[#d4a94d]">
                            <a href="#" className="hover:text-white transition-colors">Termos</a>
                            <a href="#" className="hover:text-white transition-colors">Privacidade</a>
                            <a href="#" className="hover:text-white transition-colors">Suporte</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
}
